interface RGB {
  r: number;
  g: number;
  b: number;
}

interface HSV {
  h: number;
  s: number;
  v: number;
}

interface Color {
  hex: string;
  hsv: HSV;
  rgb: RGB;
}

function toHex(value: string): string {
  if (!value.startsWith("#")) {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) throw new Error("No canvas 2d");
    ctx.fillStyle = value;
    return ctx.fillStyle;
  } else if (value.length === 4 || value.length === 5) {
    return (
      "#" +
      value
        .slice(1)
        .split("")
        .map((v) => v + v)
        .join("")
    );
  }
  return value;
}

function hex2rgb(hex: string): RGB {
  const arr = hex
    .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => ["#", r, r, g, g, b, b].join(""))
    .substring(1)
    .match(/.{2}/g)
    ?.map((x) => parseInt(x, 16)) ?? [0, 0, 0];

  return { r: arr[0]!, g: arr[1]!, b: arr[2]! };
}

function rgb2hsv({ r, g, b }: RGB): HSV {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  const h = d
    ? (max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? 2 + (b - r) / d : 4 + (r - g) / d) *
      60
    : 0;
  const s = max ? (d / max) * 100 : 0;
  const v = max * 100;

  return { h, s, v };
}

function hsv2rgb({ h, s, v }: HSV): RGB {
  s /= 100;
  v /= 100;

  const i = Math.floor(h / 60);
  const f = h / 60 - i;
  const p = v * (1 - s);
  const q = v * (1 - s * f);
  const t = v * (1 - s * (1 - f));

  const arr = [
    [v, t, p],
    [q, v, p],
    [p, v, t],
    [p, q, v],
    [t, p, v],
    [v, p, q],
  ][i % 6];

  return {
    r: Math.round(arr![0]! * 255),
    g: Math.round(arr![1]! * 255),
    b: Math.round(arr![2]! * 255),
  };
}

function rgb2hex({ r, g, b }: RGB): string {
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

export function transformColor<M extends keyof Color>(format: M, incoming: Color[M]): Color {
  let hex;
  let rgb: RGB;
  let hsv: HSV;

  if (format === "hex") {
    hex = toHex(incoming as string);
    rgb = hex2rgb(hex);
    hsv = rgb2hsv(rgb);
  } else if (format === "rgb") {
    rgb = incoming as RGB;
    hex = rgb2hex(rgb);
    hsv = rgb2hsv(rgb);
  } else {
    hsv = incoming as HSV;
    rgb = hsv2rgb(hsv);
    hex = rgb2hex(rgb);
  }

  return { hex, rgb, hsv };
}

export function clamp(v: number, max: number, min: number): number {
  return v > max ? max : v < min ? min : v;
}
